import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  fetchTeamList,
  getUserOrganizationList,
} from "../../services/dashboardService";
import CustomDropdownWithCheckboxes from "./CustomDropdownWithCheckboxes";
import { IDashboardHeader } from './dashboard.types';
import toast from "react-hot-toast";
import { manageReportSubscription } from "../../services/userService";
import { MailCheck, RefreshCw, Calendar, Settings } from "lucide-react";
import ReportSubscriptionPopup from "./ReportSubscriptionPopup";
import logger, { Logger } from '../../utils/logger';
import { DashboardDateManager } from './dashboardManager';
import DashboardFilterPanel, { FilterValues } from './DashboardFilterPanel';

const DashboardHeader = ({
  fromDate,
  isCustomDate,
  customFromDate,
  customToDate,
  selectFilterDate,
  handleFilteronChange,
  selectFilterUser,
  handleRefresh,
  onToggleVisibilitySettings,
  onListSelectionChange,
  selectedTeamId,
  selectedUserId,
  isOrganization,
  onApplyFilters, // 🆕 NEW: Batch filter application
  onClearFilters, // 🆕 NEW: Clear all filters
  selectedListIds, // 🆕 NEW: Current selected lists
}: IDashboardHeader & {
  onToggleVisibilitySettings: () => void;
  selectedTeamId?: string;
  selectedUserId?: string;
  isOrganization?: boolean;
  onApplyFilters?: (filters: FilterValues) => void;
  onClearFilters?: () => void;
  selectedListIds?: string[];
}) => {
  const [teamData, setTeamData] = useState<any>([]);
  const [organizationData, setOrganizationData] = useState<any>([]);
  const [allUsersData, setAllUsersData] = useState<any>([]); // 🎯 Store ALL users
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [showEmailConfirmModal, setShowEmailConfirmModal] = useState(false);
  const [emailPrefLoading, setEmailPrefLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false); // 🆕 NEW: Loading state for refresh
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>(
    []
  );

  // 🎯 Filter users by selected team
  const filterUserData = useMemo(() => {
    if (!selectedTeamId || selectedTeamId === '') {
      return allUsersData; // Show all users if no team selected
    }

    // Filter users who belong to the selected team
    const filteredUsers = allUsersData.filter((user: any) => {
      // Check if user's teams array contains the selected team
      return user.teams?.some((teamId: string) => teamId === selectedTeamId);
    });

    logger.log('🎯 Filtered team members:', {
      selectedTeam: selectedTeamId,
      totalUsers: allUsersData.length,
      filteredUsers: filteredUsers.length
    });

    return filteredUsers;
  }, [selectedTeamId, allUsersData]);

  // Memoized click handlers to prevent re-renders
  const handleFromDateClick = useCallback(() => {
    const input = document.querySelector('input[name="from"]') as HTMLInputElement;
    input?.showPicker?.();
  }, []);

  const handleToDateClick = useCallback(() => {
    const input = document.querySelector('input[name="to"]') as HTMLInputElement;
    input?.showPicker?.();
  }, []);

  // Memoized styles to prevent object recreation
  const dateInputStyle = useMemo(() => ({
    backgroundColor: "white",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    height: "36px",
    fontSize: "14px",
    fontWeight: "500",
    color: "#334155",
    padding: "0 12px 0 40px",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    cursor: "pointer",
    width: "180px",
    appearance: "none" as const,
    WebkitAppearance: "none" as const
  }), []);

  const dropdownStyle = useMemo(() => ({
    backgroundColor: "white",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    height: "36px",
    fontSize: "14px",
    fontWeight: "500",
    color: "#334155",
    padding: "0 12px",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    cursor: "pointer",
    appearance: "none" as const,
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundPosition: "right 8px center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "16px",
    paddingRight: "32px"
  }), []);

  const dateContainerStyle = useMemo(() => ({
    position: "relative" as const,
    display: "inline-block" as const,
    cursor: "pointer"
  }), []);

  const calendarIconStyle = useMemo(() => ({
    position: "absolute" as const,
    left: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#6b7280",
    pointerEvents: "none" as const
  }), []);

  const getDisplayText = () => {
    const selectedValues = [...selectedTeams, ...selectedOrganizations];
    
    if (!selectedValues?.length) return "Select Options";
  
    const selectedOptions = options.filter(opt => 
      selectedValues.includes(opt.value)
    );
  
    if (selectedOptions.length === 1) {
      return selectedOptions[0].label.split(" - ")[0];
    }
  
    const firstTwo = selectedOptions.slice(0, 2).map(opt => 
      opt.label.split(" - ")[0]
    );
    
    if (selectedOptions.length > 2) {
      const othersCount = selectedOptions.length - 2;
      return `${firstTwo.join(", ")} & ${othersCount} ${othersCount === 1 ? 'other' : 'others'}`;
    }
  
    return firstTwo.join(" & ");
  };
  
  const StoreData = useSelector((state: any) => {
    return state?.rootReducers;
  });

  // logger.log('🔥 FULL STORE DATA:', StoreData); // DISABLED: Causes spam on every render
  // logger.log('🔥 USER DETAILS:', StoreData?.userData?.userDetails); // DISABLED
  // logger.log('🔥 USER ROLE:', StoreData?.userData?.userDetails?.role?.name); // DISABLED
  // logger.log('🔥 SUBORDINATE DATA:', StoreData?.userData?.userDetails?.subordinateEmployees); // DISABLED

  const handleEmailPreference = async (frequencies: string[]) => {
    setEmailPrefLoading(true);
    try {
      await manageReportSubscription(frequencies, true);

      // Build a friendly message based on selected frequencies
      const frequencyLabels = frequencies.map(f => {
        switch(f) {
          case 'daily': return 'daily';
          case 'weekly': return 'weekly';
          case 'monthly': return 'monthly';
          default: return f;
        }
      });

      const message = frequencies.length === 1
        ? `Successfully subscribed to ${frequencyLabels[0]} reports!`
        : `Successfully subscribed to ${frequencyLabels.join(', ')} reports!`;

      toast.success(message, {
        position: "top-right",
        duration: 3000
      });
      setShowEmailConfirmModal(false);
    } catch (error) {
      console.error('Failed to update email preference:', error);
      toast.error("Unable to subscribe to reports. Please try again.", {
        position: "top-right",
        duration: 4000
      });
    } finally {
      setEmailPrefLoading(false);
    }
  };

  // 🚧 COMMENTED OUT: Handle list filter selection changes - Feature under development
  /*
  const handleListSelectionChange = (selectedListIds: string[]) => {
    logger.log('📋 Dashboard list filter changed:', {
      selectedLists: selectedListIds,
      count: selectedListIds.length
    });
    
    if (onListSelectionChange) {
      onListSelectionChange(selectedListIds);
    }
  };
  */

  // 🆕 NEW: Handle refresh button click
  const handleRefreshClick = async () => {
    logger.log('🔄 Dashboard refresh button clicked');
    setIsRefreshing(true);
    
    try {
      // Call the refresh function passed from parent
      await handleRefresh();
      
      // Show success message
      toast.success("Dashboard data refreshed successfully!", {
        position: "top-right",
        duration: 2000
      });
    } catch (error) {
      console.error('❌ Refresh failed:', error);
      toast.error("Failed to refresh dashboard data", {
        position: "top-right", 
        duration: 3000
      });
    } finally {
      setIsRefreshing(false);
    }
  };

useEffect(() => {
  logger.log('🔥 useEffect triggered');
  
  // Get user role
  const userRole = StoreData?.userData?.userDetails?.role?.name;
  logger.log('🔥 Current user role:', userRole);
  
  const subordinateData = StoreData?.userData?.userDetails?.subordinateEmployees;
  logger.log('🔥 Subordinate data:', subordinateData);

  // 🎯 SIMPLE ROLE CHECK: Super Admin vs Everyone Else
  if (userRole === "super_admin") {
    logger.log('👑 SUPER ADMIN: Using API calls');
    
    // Original API calls for super admin
    const getAllTeam = async () => {
      try {
        const response = await fetchTeamList();
        if (response && response.status) {
          logger.log('👑 Teams loaded:', response.data.data.length);
          setTeamData(response.data.data);
        }
      } catch (err) {
        Logger.log(err);
      }
    };

    const handleGetUserOrganization = async () => {
      try {
        const response = await getUserOrganizationList({
          page: 1,
          perPage: 50,
        });
        if (response && response.status) {
          let tempArray = [...response.data.data];
          tempArray.forEach((el: any) => {
            let findRole =
              StoreData?.userData?.userDetails?.organizationRoles?.find(
                (x: any) => x._id === el.role
              )?.displayName || '';
            el.roleName = findRole;
            el.organizationName =
              StoreData?.userData?.userDetails?.organization?.name || '';
          });

          let tempObj = {
            firstName: StoreData?.userData?.userDetails?.firstName || '',
            _id: StoreData?.userData?.userDetails?._id,
            organizationName:
              StoreData?.userData?.userDetails?.organization?.name || '',
            roleName: StoreData?.userData?.userDetails?.role?.displayName || '',
            teams: StoreData?.userData?.userDetails?.teams || [], // 🎯 Store user's teams
          };
          tempArray.push(tempObj);

          logger.log('👑 Users loaded:', tempArray.length);
          setAllUsersData(tempArray); // 🎯 Use allUsersData instead
        }
      } catch (err) {
        Logger.log(err, "Error");
      }
    };

    getAllTeam();
    handleGetUserOrganization();
    
  } else {
    logger.log('👤 NON-SUPER ADMIN: Using subordinate data');
    
    // For ALL OTHER ROLES: Use subordinate data
    const reportTo = subordinateData?.reportTo || [];
    const teamMembers = subordinateData?.teamMembers || [];
    const orgMembers = subordinateData?.organizationMember || [];
    const teams = subordinateData?.teams || [];
    
    logger.log('👤 reportTo:', reportTo.length);
    logger.log('👤 teamMembers:', teamMembers.length);
    logger.log('👤 orgMembers:', orgMembers.length);
    logger.log('👤 teams:', teams.length);
    
    // Set teams
    setTeamData(teams);
    
    // Union all users and remove duplicates
    const allUsers = [...reportTo, ...teamMembers, ...orgMembers];
    logger.log('👤 Total users before dedup:', allUsers.length);
    
    const uniqueUsers = allUsers.filter((user, index, array) => 
      array.findIndex(u => u._id === user._id) === index
    );
    
    logger.log('👤 Unique users after dedup:', uniqueUsers.length);
    
    // Process users to add role names
    uniqueUsers.forEach((user: any) => {
      let findRole =
        StoreData?.userData?.userDetails?.organizationRoles?.find(
          (x: any) => x._id === user.role
        )?.displayName || user.role || '';
      user.roleName = findRole;
      user.organizationName =
        StoreData?.userData?.userDetails?.organization?.name || '';
      // 🎯 Ensure teams array exists
      if (!user.teams) user.teams = [];
    });

    logger.log('👤 Final users for non-super admin:', uniqueUsers.length);
    setAllUsersData(uniqueUsers); // 🎯 Use allUsersData instead
  }

  // 🔒 SECURITY FIX: Only super_admin gets organization option
  if (userRole === 'super_admin') {
    const getOrganizationData = () => {
      let tempOrgArray = [];
      let tempOrgData = {
        name: StoreData?.userData?.userDetails?.organization?.name,
        _id: StoreData?.userData?.userDetails?.organization?._id,
      };
      tempOrgArray.push(tempOrgData);
      setOrganizationData(tempOrgArray);
      logger.log('👑 Organization option enabled for super_admin');
    };

    getOrganizationData();
  } else {
    // 🔒 OTHER ROLES: No organization option
    setOrganizationData([]);
    logger.log('🔒 Organization option disabled for non-admin');
  }
}, [StoreData?.userData?.userDetails]);

  const options = useMemo(() => [
    ...teamData.map((item: any) => ({
      label: `${item.name} - Team`,
      value: item._id,
    })),
    ...organizationData.map((item: any) => ({
      label: `${item.name} - Organization`,
      value: item._id,
    })),
  ], [teamData, organizationData]);

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between">
        <div className="fs-4 fw-bold text-dark">Dashboard</div>
        <div className="dashboard_header">
          <button
            onClick={() => setShowEmailConfirmModal(true)}
            disabled={emailPrefLoading}
            style={{
              backgroundColor: "#06402b",
              color: "white",
              borderRadius: "8px",
              height: "36px",
              padding: "0 16px",
              border: "none",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginRight: "12px",
              transition: "all 0.2s ease",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
              boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)"
            }}
            className="hover:bg-indigo-600 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            <MailCheck size={18} />
            {emailPrefLoading ? "Processing..." : "Get Daily Email Reports"}
          </button>

          {/* 🚀 NEW: Consolidated Filter Panel */}
          {onApplyFilters && onClearFilters && (
            <DashboardFilterPanel
              currentFilters={{
                dateRange: fromDate,
                customFromDate: isCustomDate ? customFromDate : undefined,
                customToDate: isCustomDate ? customToDate : undefined,
                teamOrOrg: selectedTeamId || (isOrganization ? 'organization' : ''),
                teamMember: selectedUserId || '',
                listIds: selectedListIds || []
              }}
              teamData={teamData}
              organizationData={organizationData}
              userData={filterUserData}
              onApplyFilters={onApplyFilters}
              onClearFilters={onClearFilters}
              getDisplayText={getDisplayText}
            />
          )}
        </div>
      </div>
      {/* Custom date inputs now in FilterPanel */}
      <ReportSubscriptionPopup
        isOpen={showEmailConfirmModal}
        onClose={() => setShowEmailConfirmModal(false)}
        onSubscribe={handleEmailPreference}
        loading={emailPrefLoading}
      />

      {/* 🆕 NEW: Add CSS for refresh button animation and hover effects */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .dashboard-select:hover {
          border-color: #3b82f6 !important;
        }
        .dashboard-date-input:hover {
          border-color: #3b82f6 !important;
        }
      `}</style>
    </div>
  );
};

export default DashboardHeader;